import './liveDataDiscovery.less'
import {
  ACTION_SCANNED_DATA_GRAPH_FETCH,
  fetchScannedDataWidgets,
  fetchScannedDataCount,
  resetWidgets,
  ScannedDataGraphItem,
  ScannedDataParams,
  SCANNED_RANGE,
  DocumentsScannedParams,
  ACTION_SCANNED_DATA_COUNT_FETCH
} from '../../../scannedData/scannedDataSlice'
import CirclePulseIcon from '../../../../assets/inlineSvg/circle-pulse-icon.svg'
import {
  ACTION_ATTRIBUTE_INSTANCES_COUNT_FETCH,
  fetchAttributeInstancesCount,
  IGetAttributeInstancesCountParams
} from '../../../attributes/attributesSlice'
import {
  DATA_SOURCE_ID,
  FILTER_ATTRIBUTE_HAS_INSTANCES,
  FILTER_SENSITIVITY_MULTIPLE,
  FILTER_SENSITIVITY_VALUES,
  URL_ATTRIBUTES,
  URL_DATA_SOURCES,
  URL_INSIGHTS
} from '../../../../constants'
import useUrlContext from '../../../../hooks/useUrlContext'
import ChartBar from '../../../../components/ChartBarSimple/chartBarSimple'
import { formatDate, formatTime } from '../../../../utils/timeUtil'
import { humanDiskSize } from '../../../../utils/mathUtils'
import { getShowLoader } from '../../../../reducers/requestReducer'
import LoaderView from '../../../../components/Loader/loader'
import { changeUrl, getUpdatedUrl } from '../../../../utils/urlUtil'
import ChartArea from '../../../../components/ChartArea/chartArea'
import { RootState } from '../../../../rootReducer'
import { ChartColors, getUniqueWidgetIndex, UNIQUE_WIDGET_INDEX } from '../constants'
import { mockLiveDataDiscoveryWidgetData } from '../../renderer/utils/widgetMockdata'
import { connect } from 'react-redux'
import React, { useEffect, useState } from 'react'
import { useIntl } from 'react-intl'
import { Link } from 'react-router-dom'
import dayjs from 'dayjs'
import { Typography } from '@lightbeamai/design-system'

interface OwnProps {
  useMockData?: boolean
  parentUrl?: string
  widgetDashboardId?: string
  widgetDatasourceId?: string
}

interface StateProps {
  attributes?: ScannedDataGraphItem[]
  documents?: ScannedDataGraphItem[]
  totalDataScanned?: number
  showLoader: boolean
  totalAttributeInstances?: number
}

interface DispatchProps {
  fetchScannedDataWidgets: (params: ScannedDataParams & { [UNIQUE_WIDGET_INDEX]: string }) => void
  fetchAttributeInstancesCount: (
    params: IGetAttributeInstancesCountParams & { [UNIQUE_WIDGET_INDEX]: string }
  ) => void
  fetchScannedDataCount: (
    params: DocumentsScannedParams & { [UNIQUE_WIDGET_INDEX]: string }
  ) => void
  resetWidgets: (widgetIndex: string) => void
}

type LiveDataDiscoveryComponentProps = StateProps & DispatchProps & OwnProps

const LiveDataDiscovery = (props: LiveDataDiscoveryComponentProps) => {
  const {
    attributes = [],
    documents = [],
    totalDataScanned = 0,
    showLoader,
    totalAttributeInstances = 0,
    useMockData = false,
    widgetDashboardId,
    widgetDatasourceId,
    fetchScannedDataCount,
    fetchScannedDataWidgets,
    fetchAttributeInstancesCount,
    resetWidgets,
    parentUrl = ''
  } = props

  const intl = useIntl()
  const context = useUrlContext()
  const datasourceId = context.contextFilters[DATA_SOURCE_ID]

  const widgetIndex = getUniqueWidgetIndex(widgetDashboardId, widgetDatasourceId)

  const [range, setRange] = useState<SCANNED_RANGE>(SCANNED_RANGE.week)
  const barToolTipFormatter = (val) => humanDiskSize(val)
  useEffect(() => {
    if (useMockData) return
    fetchScannedDataCount({
      ...(datasourceId ? { datasourceId } : {}),
      [UNIQUE_WIDGET_INDEX]: widgetIndex
    })
  }, [datasourceId, fetchScannedDataCount, useMockData, widgetIndex])

  useEffect(() => {
    if (useMockData) return
    const instancesCountParams = {
      ...(datasourceId ? { datasourceIds: [datasourceId] } : {}),
      [UNIQUE_WIDGET_INDEX]: widgetIndex
    }
    fetchAttributeInstancesCount(instancesCountParams)
  }, [datasourceId, fetchAttributeInstancesCount, useMockData, widgetIndex])

  useEffect(() => {
    if (useMockData) return
    fetchScannedDataWidgets({
      datasourceId,
      range,
      [UNIQUE_WIDGET_INDEX]: widgetIndex
    })
  }, [datasourceId, range, fetchScannedDataWidgets, useMockData, widgetIndex])

  useEffect(() => {
    return () => resetWidgets(widgetIndex)
  }, [resetWidgets, widgetIndex])

  const docsSorted = JSON.parse(JSON.stringify(documents)).sort((docFirst, docSecond) =>
    dayjs(docFirst.timestamp).diff(dayjs(docSecond.timestamp))
  )
  const documentsChartData = docsSorted.map((item) => ({
    name:
      range === SCANNED_RANGE.week ? formatDate(item.timestamp, true) : formatTime(item.timestamp),
    value: item.value
  }))

  const attributesSorted = JSON.parse(JSON.stringify(attributes)).sort((attrFirst, attrSecond) =>
    dayjs(attrFirst.timestamp).diff(dayjs(attrSecond.timestamp))
  )
  const attributesChartDate = attributesSorted.map((item) => ({
    name:
      range === SCANNED_RANGE.week ? formatDate(item.timestamp, true) : formatTime(item.timestamp),
    value: item.value
  }))

  const handleGoToDataSourcesPage = () => !datasourceId && changeUrl(URL_DATA_SOURCES)
  const baseUrl = parentUrl ? parentUrl : context.pathname
  const attributeLink = getUpdatedUrl({
    pathName: baseUrl + URL_ATTRIBUTES,
    pageName: URL_ATTRIBUTES,
    ...(parentUrl === URL_INSIGHTS && {
      pageQueryParams: {
        [FILTER_ATTRIBUTE_HAS_INSTANCES]: 'Yes',
        [FILTER_SENSITIVITY_MULTIPLE]: FILTER_SENSITIVITY_VALUES.high
      }
    }),
    resetPageQuery: true
  })
  const handleGoToDataAttributesPage = () => changeUrl(attributeLink)
  const documentsLabelFormatter = (value) =>
    String(Math.round(parseFloat(humanDiskSize(value).split(' ')[0])))

  const attributesLabelFormatter = (value) => intl.formatNumber(value, { notation: 'compact' })

  return (
    <div className="lb-widget-card scanned-data-summary xs-py-16 xs-pl-16 xs-pr-16">
      <div className="live-discovery-data-widget" data-test-id="scanneddata-scanned-data-widgets">
        <div className="header-wrapper">
          <div className="header-title">
            <CirclePulseIcon />
            <Typography color="primary" variant="h6">
              {intl.formatMessage({ id: 'scannedData.widget.title' })}
            </Typography>
          </div>
          <div className="date-range-container">
            <button
              className={`btn week xs-py-4 xs-px-16 ${
                range === SCANNED_RANGE.week ? 'active' : ''
              }`}
              onClick={() => setRange(SCANNED_RANGE.week)}
            >
              <Typography>{intl.formatMessage({ id: 'scannedData.widget.week' })}</Typography>
            </button>
            <button
              className={`btn day xs-py-4 xs-px-16 ${range === SCANNED_RANGE.day ? 'active' : ''}`}
              onClick={() => setRange(SCANNED_RANGE.day)}
            >
              <Typography>{intl.formatMessage({ id: 'scannedData.widget.day' })}</Typography>
            </button>
          </div>
        </div>
        <div className="charts">
          <div className="chart documents">
            <div className="count-title">
              <Typography className="xs-mb-0" color="primary" type="strong" variant="basePlus6">
                {humanDiskSize(totalDataScanned)}
              </Typography>
              <Typography color="primary" type="normal" variant="baseMinus2">
                {intl.formatMessage({ id: 'scannedData.widget.dataScanned' })}
              </Typography>
            </div>

            <ChartBar
              yLabel={intl.formatMessage({ id: 'scannedData.widget.xLabel' })}
              onClick={handleGoToDataSourcesPage}
              data={documentsChartData}
              yLabelFormatter={documentsLabelFormatter}
              YTickFormatter={documentsLabelFormatter}
              barColor={ChartColors.Blue}
              onBarItemClick={handleGoToDataSourcesPage}
              className="xs-pt-8 v2-chart"
              toolTipFormatter={barToolTipFormatter}
              cartesianGridHorrizontal
              xAxisAngle={0}
              tickFormatter={{ fontSize: '0.72rem', fill: ChartColors.SecondaryText }}
              yAxisLabelPadding={{ dx: -12, dy: -6 }}
            />
            <LoaderView size="small" showLoader={showLoader} />
          </div>
          <div className="chart xs-mt-0 attributes">
            <Link to={attributeLink}>
              <div className="count-title">
                <Typography className="xs-mb-0" color="primary" type="strong" variant="basePlus6">
                  {intl.formatNumber(totalAttributeInstances, { notation: 'compact' })}
                </Typography>
                <Typography color="primary" type="normal" variant="baseMinus2">
                  {intl.formatMessage({ id: 'scannedData.widget.attributesFound' })}
                </Typography>
              </div>

              <ChartArea
                onClick={handleGoToDataAttributesPage}
                data={attributesChartDate}
                yLabelFormatter={attributesLabelFormatter}
                areaColor={ChartColors.Purple}
                strokeColor={ChartColors.Purple}
                className="xs-mt-0 v2-chart xs-pt-8"
                cartesianGridHorrizontal
                tickFormatter={{ fontSize: '0.72rem', fill: ChartColors.SecondaryText }}
              />
            </Link>
          </div>
        </div>
      </div>
    </div>
  )
}

const mapStateToProps = (state: RootState, ownProps: OwnProps): StateProps => {
  const widgetIndex = getUniqueWidgetIndex(ownProps.widgetDashboardId, ownProps.widgetDatasourceId)
  return {
    attributes: ownProps?.useMockData
      ? mockLiveDataDiscoveryWidgetData.attributes
      : state.scannedData[widgetIndex]?.graphAttributes,
    documents: ownProps?.useMockData
      ? mockLiveDataDiscoveryWidgetData.documents
      : state.scannedData[widgetIndex]?.graphDocuments,
    totalDataScanned: ownProps?.useMockData
      ? mockLiveDataDiscoveryWidgetData.totalDataScanned
      : state.scannedData[widgetIndex]?.totalDataScanned,
    totalAttributeInstances: ownProps?.useMockData
      ? mockLiveDataDiscoveryWidgetData.totalAttributeInstances
      : state.attributes.attributes.widgetInstancesCount?.[widgetIndex],
    showLoader:
      getShowLoader(ACTION_SCANNED_DATA_GRAPH_FETCH) ||
      getShowLoader(ACTION_ATTRIBUTE_INSTANCES_COUNT_FETCH) ||
      getShowLoader(ACTION_SCANNED_DATA_COUNT_FETCH)
  }
}

const mapDispatchToProps: DispatchProps = {
  fetchScannedDataWidgets,
  fetchAttributeInstancesCount,
  fetchScannedDataCount,
  resetWidgets
}

export default connect<StateProps, DispatchProps, OwnProps, RootState>(
  mapStateToProps,
  mapDispatchToProps
)(LiveDataDiscovery)
